import React from 'react';
import {
  ExclamationCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon
} from '@heroicons/react/24/outline';

const IssuesCounterWidget = ({ currentMonth, previousMonth, loading = false }) => {
  const totalCurrent = currentMonth?.total || 0;
  const totalPrevious = previousMonth?.total || 0;
  const change = totalCurrent - totalPrevious;
  const changePercent = totalPrevious > 0 ? ((change / totalPrevious) * 100) : 0;

  // Severity breakdown
  const critical = currentMonth?.critical || 0;
  const high = currentMonth?.high || 0;
  const medium = currentMonth?.medium || 0;
  const low = currentMonth?.low || 0;

  const SeverityBadge = ({ count, color, label }) => (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-3 rounded-full ${color}`} />
      <span className="text-sm text-gray-700">
        <span className="font-semibold">{count}</span> {label}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 bg-gray-200 rounded w-36 animate-pulse"></div>
          <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>

        {/* Large Counter Skeleton */}
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="h-14 bg-gray-200 rounded w-20 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
          </div>
        </div>

        {/* Severity Breakdown Skeleton */}
        <div className="space-y-2 mb-4 flex-1 flex flex-col justify-center">
          <div className="h-3 bg-gray-200 rounded w-20 mb-2 animate-pulse"></div>
          <div className="grid grid-cols-2 gap-3">
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-200 animate-pulse" />
                <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Status Distribution Skeleton */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-200 animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-200 animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Trend Indicator Skeleton */}
        <div className="mt-4 p-3 rounded-lg bg-gray-100 border border-gray-200">
          <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Issues This Month</h3>
        <div className="p-2 rounded-lg bg-blue-100">
          <ExclamationCircleIcon className="h-6 w-6 text-blue-600" />
        </div>
      </div>

      {/* Large Counter Display */}
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-5xl font-bold text-gray-900">
            {loading ? '–' : totalCurrent}
          </div>
          <p className="text-sm text-gray-600 mt-1">Total Issues</p>
        </div>

        {/* Month-over-Month Change */}
        {!loading && change !== 0 && (
          <div className="text-right">
            <div className={`flex items-center gap-1 text-sm font-semibold ${
              change < 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {change < 0 ? (
                <ArrowTrendingDownIcon className="h-4 w-4" />
              ) : (
                <ArrowTrendingUpIcon className="h-4 w-4" />
              )}
              <span>
                {Math.abs(changePercent).toFixed(1)}%
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">vs last month</p>
          </div>
        )}

        {!loading && change === 0 && totalCurrent > 0 && (
          <div className="text-right">
            <div className="flex items-center gap-1 text-sm font-semibold text-gray-600">
              <MinusIcon className="h-4 w-4" />
              <span>No change</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">vs last month</p>
          </div>
        )}
      </div>

      {/* Severity Breakdown */}
      <div className="space-y-2 mb-4 flex-1 flex flex-col justify-center">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
          By Severity
        </p>
        <div className="grid grid-cols-2 gap-3">
          <SeverityBadge count={critical} color="bg-red-500" label="Critical" />
          <SeverityBadge count={high} color="bg-orange-500" label="High" />
          <SeverityBadge count={medium} color="bg-yellow-500" label="Medium" />
          <SeverityBadge count={low} color="bg-blue-500" label="Low" />
        </div>
      </div>

      {/* Status Distribution */}
      <div className="pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-gray-700">Open: <span className="font-semibold">{currentMonth?.open || 0}</span></span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-gray-700">Resolved: <span className="font-semibold">{currentMonth?.resolved || 0}</span></span>
          </div>
        </div>
      </div>

      {/* Trend Indicator */}
      {!loading && (
        <div className={`mt-4 p-3 rounded-lg ${
          change < 0 ? 'bg-green-50 border-green-200' :
          change > 0 ? 'bg-red-50 border-red-200' :
          'bg-gray-50 border-gray-200'
        } border`}>
          <p className={`text-sm font-medium flex items-center ${
            change < 0 ? 'text-green-800' :
            change > 0 ? 'text-red-800' :
            'text-gray-800'
          }`}>
            {change < 0 ? (
              <>
                <ArrowTrendingDownIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                {`${Math.abs(change)} fewer issues than last month`}
              </>
            ) : change > 0 ? (
              <>
                <ArrowTrendingUpIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                {`${change} more issues than last month`}
              </>
            ) : (
              <>
                <MinusIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                Same number of issues as last month
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default IssuesCounterWidget;
